import React from 'react';
import { Scan, Upload, Palette, Wallet, Printer, ArrowRight } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      step: 1,
      title: 'Scan QR Code',
      description: 'Use your phone camera to scan the QR code displayed on the PRINTLY kiosk screen',
      icon: Scan,
      color: 'yellow'
    },
    {
      step: 2,
      title: 'Upload Document',
      description: 'Select your file from cloud storage or device. Supports PDF, DOCX, and images',
      icon: Upload,
      color: 'yellow'
    },
    {
      step: 3,
      title: 'Choose Print Type',
      description: 'Select Black & White or Color printing. Choose paper size (A4/A5)',
      icon: Palette,
      color: 'yellow'
    },
    {
      step: 4,
      title: 'Make Payment',
      description: 'Pay securely through UPI, cards, or digital wallets. Instant confirmation',
      icon: Wallet,
      color: 'yellow'
    },
    {
      step: 5,
      title: 'Print & Collect',
      description: 'Get instant prints or schedule for later pickup. Receive notification when ready',
      icon: Printer,
      color: 'yellow'
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-br from-gray-50 to-yellow-50/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-yellow-500 font-semibold text-sm uppercase tracking-wide">Simple Process</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-6">
            How PRINTLEY Works
          </h2>
          <p className="text-lg text-gray-600">
            Five simple steps to get your documents printed. No app installation, no account creation required.
          </p>
        </div>

        {/* Steps - Desktop View */}
        <div className="hidden lg:block relative">
          <div className="grid grid-cols-5 gap-8">
            {steps.map((item, index) => (
              <div key={index} className="relative">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="absolute top-16 left-[60%] w-[80%] h-0.5 bg-yellow-300">
                    <ArrowRight className="absolute -right-2 -top-2 w-5 h-5 text-yellow-500" />
                  </div>
                )}
                
                {/* Step Card */}
                <div className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-yellow-200">
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-10 h-10 bg-yellow-500 text-gray-900 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                    {item.step}
                  </div>

                  {/* Icon */}
                  <div className="w-16 h-16 bg-yellow-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <item.icon className="w-8 h-8 text-yellow-600" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Steps - Mobile/Tablet View */}
        <div className="lg:hidden space-y-6">
          {steps.map((item, index) => (
            <div key={index} className="relative">
              {/* Connector */}
              {index < steps.length - 1 && (
                <div className="absolute left-8 top-20 w-0.5 h-12 bg-yellow-300"></div>
              )}

              <div className="flex gap-6 bg-white rounded-2xl p-6 shadow-lg">
                {/* Left - Step Number & Icon */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-yellow-500 text-gray-900 rounded-xl flex items-center justify-center font-bold text-2xl shadow-lg mb-3">
                    {item.step}
                  </div>
                  <div className="w-16 h-16 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <item.icon className="w-8 h-8 text-yellow-600" />
                  </div>
                </div>

                {/* Right - Content */}
                <div className="flex-1 pt-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-block bg-white rounded-2xl p-8 shadow-lg border-2 border-yellow-200">
            <p className="text-lg text-gray-900 font-semibold mb-2">
              Ready in under 30 seconds! ⚡
            </p>
            <p className="text-gray-600">
              From scan to print, PRINTLEY is the fastest way to get your documents.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
